"use client";
import { useState, useRef, useEffect } from "react";
import { Tabs, TabsList, TabsTrigger } from "~/components/v4/shadcn/Tabs";
import Link from "next/link";
import { GetHomePeople, PeopleTabType } from "../actions";
import { slugifyUser } from "~/utils/string";
import { If } from "~/components/v3/Conditional";
import { useVirtualizer } from "@tanstack/react-virtual";
import { UserAvatar } from "~/components/v4/shadcn/UserAvatar";
import { ScrollableContainer } from "./ScrollableContainer";
import { cn } from "~/utils/fontsConfig";
import { HostTooltip } from "~/components/v3/events-components/HostedByV3";
import { useBreakpoint } from "~/hooks/useIsMobile";
import { FollowUserButton } from "~/components/v4/themed/user/FollowUserButton";

const HomePeople = ({ peopleData }: { peopleData: GetHomePeople }) => {
    const [activeTab, setActiveTab] = useState<PeopleTabType>("followers");
    // TODO: Fix the type here? It wasn't able to infer any of the properties on GetHomePeople
    // for some reason GetHomePeople["followers"] etc was also throwing an error even though this return
    // type CLEARLY has the fucking properties :REEEE: forgive me other devs, throwing an any here because
    // it works and idgaf about the types rn
    const [users, setUsers] = useState<any>([]);
    const isSmallScreen = useBreakpoint("sm");

    useEffect(() => {
        if (peopleData) {
            setUsers(peopleData[activeTab] || []);
        }
    }, [peopleData, activeTab]);

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    // Set up the virtualizer
    const rowVirtualizer = useVirtualizer({
        count: users.length,
        getScrollElement: () => scrollContainerRef.current,
        estimateSize: () => 60, // Estimated height of each row in pixels
        overscan: 5, // Number of items to render before/after the visible area
    });

    return (
        <div className="flex h-full flex-col">
            <Tabs
                value={activeTab}
                onValueChange={(value) => setActiveTab(value as PeopleTabType)}
                className="w-full" // Removed flex and justify-center
            >
                <TabsList className="h-auto justify-start px-4 py-0">
                    {" "}
                    {/* Changed justify-center to justify-start */}
                    <TabsTrigger
                        value="followers"
                        className="flex flex-row items-center py-1 text-sm"
                    >
                        {peopleData?.counts?.followers.toLocaleString("en-US")}{" "}
                        Followers
                    </TabsTrigger>
                    <TabsTrigger
                        value="following"
                        className="flex flex-row items-center py-1 text-sm"
                    >
                        {peopleData?.counts?.following.toLocaleString("en-US")}{" "}
                        Following
                    </TabsTrigger>
                    <TabsTrigger
                        value="mutuals"
                        className="flex flex-row items-center py-1 text-sm"
                    >
                        {peopleData?.counts?.mutuals.toLocaleString("en-US")}{" "}
                        Mutuals
                    </TabsTrigger>
                    <TabsTrigger
                        value="hosts"
                        className="flex flex-row items-center py-1 text-sm"
                    >
                        {peopleData?.counts?.hosts.toLocaleString("en-US")}{" "}
                        Nearby Hosts
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            <If condition={activeTab === "followers"}>
                <span className="ml-6 mt-2 text-v3-N300">
                    Invite your followers to events. Grow your following by
                    hosting within an existing communtiy.
                </span>
            </If>
            <If condition={activeTab === "following"}>
                <span className="ml-6 mt-2 text-v3-N300">
                    People who can invite you to their events.
                </span>
            </If>
            <If condition={activeTab === "mutuals"}>
                <span className="ml-6 mt-2 text-v3-N300">
                    People you've attended events with.
                </span>
            </If>
            <If condition={activeTab === "hosts"}>
                <span className="ml-6 mt-2 text-v3-N300">
                    People hosting events near you.
                </span>
            </If>

            {/* The fixed height container with overflow */}
            <ScrollableContainer
                ref={scrollContainerRef}
                wrapperClassName={cn(
                    "flex-grow",
                    rowVirtualizer.getVirtualItems().length > 0
                        ? "min-h-[60vh]"
                        : "min-h-10px",
                )}
                className="absolute bottom-0 top-0 w-full"
            >
                {/* inner div with the total size */}
                <div
                    style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
                    className="relative w-full"
                >
                    {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                        const user = users[virtualItem.index];
                        return (
                            <div
                                key={user?.id ?? "N/A" + Math.random()} // this needs to be the user id due to the following button needing to know the user id
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: `${virtualItem.size}px`,
                                    transform: `translateY(${virtualItem.start}px)`,
                                    padding: "0 16px",
                                }}
                            >
                                {user && (
                                    <div className="group flex flex-row items-center py-2">
                                        <div className="flex w-full">
                                            {/* Place the entire row in a tooltip but exclude the follow button */}
                                            <HostTooltip
                                                user={user}
                                                triggerClassName="flex items-center"
                                                side={
                                                    isSmallScreen
                                                        ? "top"
                                                        : "left"
                                                }
                                            >
                                                <div
                                                    className="flex cursor-pointer items-center"
                                                    onClick={() => {
                                                        window.open(
                                                            `/profile/${slugifyUser(
                                                                {
                                                                    name: user.name,
                                                                    id: user.id,
                                                                    slug: user.slug,
                                                                },
                                                            )}`,
                                                            "_blank",
                                                        );
                                                    }}
                                                >
                                                    {/* Avatar */}
                                                    <div className="relative rounded-full border-2 border-primary transition-[border-color] duration-300 group-hover:border-secondary">
                                                        <UserAvatar
                                                            user={user}
                                                            width={40}
                                                            height={40}
                                                            size={10}
                                                        />
                                                        <If
                                                            condition={
                                                                activeTab ===
                                                                "mutuals"
                                                            }
                                                        >
                                                            <div
                                                                className={cn(
                                                                    "absolute right-0 top-0",
                                                                    "-translate-y-1/3 translate-x-1/3",
                                                                    "rounded-full bg-v3-N500/80 px-2 py-1",
                                                                    "text-xs text-v3-N300 transition-opacity duration-700",
                                                                    "opacity-100 group-hover:opacity-0",
                                                                )}
                                                            >
                                                                {
                                                                    (
                                                                        user as any
                                                                    )
                                                                        .commonEvents
                                                                }
                                                            </div>
                                                        </If>
                                                    </div>

                                                    {/* User name */}
                                                    <div className="ml-4 text-left text-lg text-white group-hover:text-v4-primary">
                                                        <span>{user.name}</span>
                                                    </div>
                                                </div>
                                            </HostTooltip>

                                            {/* Follow button outside tooltip */}
                                            <If
                                                condition={
                                                    activeTab !== "following"
                                                }
                                            >
                                                <div className="ml-auto">
                                                    <FollowUserButton
                                                        userId={user.id}
                                                        isFollowing={
                                                            user.isFollowing
                                                        }
                                                    />
                                                </div>
                                            </If>
                                        </div>

                                        {/* Hidden link for SEO/accessibility - won't be displayed but provides navigation */}
                                        <Link
                                            href={`/profile/${slugifyUser({
                                                name: user.name,
                                                id: user.id,
                                                slug: user.slug,
                                            })}`}
                                            target="_blank"
                                            className="hidden"
                                            aria-hidden="true"
                                        >
                                            {user.name}'s profile
                                        </Link>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </ScrollableContainer>
            {users.length === 0 && (
                <div className="mb-4 mt-4 text-center text-gray-400">
                    No {activeTab} found
                </div>
            )}
        </div>
    );
};

export default HomePeople;
